import type { AppContext } from '@application/context/AppContext';
import {
  HybridRestoreService,
  type HybridRestoreReport,
  type HybridRestoreStep,
} from '@application/backup/HybridRestoreService';
import {
  describeHybridTransportError,
  type HybridBackupMeta,
} from '@application/backup/HybridBackupTransport';
import type { HybridUnlockSecret } from '@application/backup/HybridBackupCrypto';
import { confirmAction } from '@presentation/components/ConfirmDialog';
import { DataTable } from '@presentation/components/DataTable';
import { enhancePasswordFields } from '@presentation/components/PasswordVisibility';
import '@presentation/components/Modal';
import { esc } from '@presentation/shell/format';
import { openTenantTransferConfirmDialog } from '../TenantTransferConfirmDialog';
import {
  HYBRID_RESTORE_STEPS,
  hybridRestoreStepLabel,
} from './hybridRestoreStepLabels';
import { describeHybridUiError } from './describeHybridUiError';

type ModalEl = HTMLElement & { open: () => void; close: () => void };
type SecretMode = 'passphrase' | 'recoveryKey';

function formatBackupDate(iso: string): string {
  const t = Date.parse(iso);
  if (Number.isNaN(t)) return iso;
  return new Date(t).toLocaleString('fr-FR');
}

function formatSize(bytes: number | undefined): string {
  if (bytes === undefined) return '—';
  if (bytes < 1024) return `${bytes} o`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} Ko`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} Mo`;
}

function clearSensitiveInput(input: HTMLInputElement | null): void {
  if (!input) return;
  input.value = '';
}

function summarizeRestoredTables(restored: Record<string, number>): string {
  const entries = Object.entries(restored).filter(([, n]) => n > 0);
  if (entries.length === 0) return 'Aucune table restaurée.';
  return entries.map(([table, n]) => `${table} : ${n}`).join(', ');
}

/** Nom d’entreprise extrait du message service (« … »), sinon libellé de repli. */
function backupTenantNameFromMessage(message: string | undefined): string {
  if (!message) return 'entreprise inconnue';
  const match = message.match(/«\s*([^»]+)\s*»/);
  const name = match?.[1]?.trim();
  return name && name.length > 0 ? name : 'entreprise inconnue';
}

function currentTenantDisplayName(ctx: AppContext): string {
  const tenant = ctx.tenantContext.tryGetTenant();
  const name = tenant?.raison_sociale ?? tenant?.nom ?? '';
  return typeof name === 'string' ? name.trim() : '';
}

/**
 * @param completedThrough — dernière étape signalée par `onProgress` (déjà terminée),
 *   ou `null` + `startingStep` pour l’état initial avant le premier callback.
 */
function buildProgressMarkup(opts: {
  completedThrough: HybridRestoreStep | null;
  startingStep?: HybridRestoreStep;
}): string {
  const completedIdx = opts.completedThrough
    ? HYBRID_RESTORE_STEPS.indexOf(opts.completedThrough)
    : -1;
  const activeStep =
    opts.completedThrough === 'done'
      ? null
      : opts.completedThrough
        ? (HYBRID_RESTORE_STEPS[completedIdx + 1] ?? null)
        : (opts.startingStep ?? 'download');
  const liveLabel = opts.completedThrough === 'done'
    ? hybridRestoreStepLabel('done')
    : activeStep
      ? hybridRestoreStepLabel(activeStep)
      : 'En attente…';
  const items = HYBRID_RESTORE_STEPS.map((step, idx) => {
    let state: 'done' | 'active' | 'pending' = 'pending';
    if (idx <= completedIdx) state = 'done';
    else if (step === activeStep) state = 'active';
    const mark = state === 'done' ? '✓' : state === 'active' ? '●' : '○';
    return `<li data-step="${esc(step)}" data-state="${state}" style="margin:4px 0;color:${
      state === 'active' ? 'var(--accent,#008080)' : state === 'done' ? 'var(--menthe,#1e7e34)' : '#586069'
    };">${mark} ${esc(hybridRestoreStepLabel(step))}</li>`;
  }).join('');
  return `
    <p id="hybrid-restore-live" aria-live="polite" style="margin:0 0 10px;font-weight:600;">${esc(liveLabel)}</p>
    <ol style="margin:0;padding-left:1.2rem;font-size:0.9rem;">${items}</ol>
  `;
}

function renderResultMessage(report: HybridRestoreReport): { tone: 'ok' | 'err'; html: string } {
  if (report.ok) {
    const parts = [
      `<p style="margin:0 0 8px;"><strong>Restauration terminée.</strong></p>`,
      `<p style="margin:0 0 8px;">Tables restaurées : ${esc(summarizeRestoredTables(report.restored))}</p>`,
    ];
    if (report.safetyBackup?.location) {
      parts.push(
        `<p style="margin:0 0 8px;">Sauvegarde de sécurité : <code>${esc(report.safetyBackup.location)}</code></p>`,
      );
    }
    if (report.files.failed > 0) {
      parts.push(
        `<p style="margin:0;color:var(--rouge,#b42318);">Pièces jointes en échec : ${report.files.failed} (données métier restaurées).</p>`,
      );
    } else if (report.files.restored > 0) {
      parts.push(`<p style="margin:0;">Pièces jointes restaurées : ${report.files.restored}</p>`);
    }
    return { tone: 'ok', html: parts.join('') };
  }

  const detail = report.errorCode
    ? describeHybridUiError(report.errorCode, report.message)
    : (report.message ?? 'La restauration a échoué.');
  const unchanged = report.databaseUnchanged
    ? `<p style="margin:0 0 8px;"><strong>Rien n’a été modifié.</strong></p>`
    : '';
  return {
    tone: 'err',
    html: `${unchanged}<p style="margin:0;" role="alert">${esc(detail)}</p>`,
  };
}

/**
 * Panneau de restauration d’une sauvegarde Hybride chiffrée
 * (liste → secret → confirmation → progression → résultat).
 */
export function renderHybridBackupRestorePanel(host: HTMLElement, ctx: AppContext): void {
  host.innerHTML = `
    <div class="hybrid-restore-panel">
      <p style="margin:0 0 16px;color:var(--gris,#586069);font-size:0.9rem;">
        Choisissez une sauvegarde Hybride, puis saisissez votre phrase secrète ou votre clé de récupération.
        Les secrets ne sont jamais journalisés.
      </p>
      <div data-hybrid-list-status style="margin-bottom:12px;color:var(--gris,#586069);">Chargement des sauvegardes…</div>
      <div data-hybrid-list></div>
      <div data-hybrid-restore-form style="display:none;margin-top:20px;padding-top:16px;border-top:1px solid #e1e4e8;"></div>
      <div data-hybrid-progress style="display:none;margin-top:16px;"></div>
      <div data-hybrid-result style="display:none;margin-top:16px;padding:12px 14px;border-radius:6px;"></div>
    </div>
  `;

  const listStatus = host.querySelector<HTMLElement>('[data-hybrid-list-status]');
  const listHost = host.querySelector<HTMLElement>('[data-hybrid-list]');
  const formHost = host.querySelector<HTMLElement>('[data-hybrid-restore-form]');
  const progressHost = host.querySelector<HTMLElement>('[data-hybrid-progress]');
  const resultHost = host.querySelector<HTMLElement>('[data-hybrid-result]');

  if (!listStatus || !listHost || !formHost || !progressHost || !resultHost) return;

  let listedItems: HybridBackupMeta[] = [];
  let selectedBackup: HybridBackupMeta | null = null;
  let secretMode: SecretMode = 'passphrase';
  let restoring = false;

  const showResult = (report: HybridRestoreReport): void => {
    const { tone, html } = renderResultMessage(report);
    resultHost.style.display = 'block';
    resultHost.style.background = tone === 'ok' ? '#eef8f4' : '#fdecea';
    resultHost.style.border = `1px solid ${tone === 'ok' ? '#b7e0cc' : '#f5c2c0'}`;
    resultHost.innerHTML = html;
  };

  const setProgressStarting = (): void => {
    progressHost.style.display = 'block';
    progressHost.innerHTML = buildProgressMarkup({
      completedThrough: null,
      startingStep: 'download',
    });
  };

  const setProgressFromService = (completed: HybridRestoreStep): void => {
    progressHost.style.display = 'block';
    progressHost.innerHTML = buildProgressMarkup({ completedThrough: completed });
  };

  const hideProgress = (): void => {
    progressHost.style.display = 'none';
    progressHost.innerHTML = '';
  };

  const renderSecretForm = (meta: HybridBackupMeta): void => {
    selectedBackup = meta;
    resultHost.style.display = 'none';
    resultHost.innerHTML = '';
    hideProgress();
    formHost.style.display = 'block';
    formHost.innerHTML = `
      <h3 style="margin:0 0 12px;font-size:1rem;">Restaurer du ${esc(formatBackupDate(meta.created_at))}</h3>
      <fieldset style="border:0;margin:0;padding:0;">
        <legend style="font-size:0.85rem;margin-bottom:8px;">Déverrouillage</legend>
        <div style="display:flex;gap:16px;margin-bottom:12px;">
          <label style="display:flex;align-items:center;gap:6px;font-size:0.9rem;">
            <input type="radio" name="hybrid-secret-mode" value="passphrase" ${secretMode === 'passphrase' ? 'checked' : ''} />
            Phrase secrète
          </label>
          <label style="display:flex;align-items:center;gap:6px;font-size:0.9rem;">
            <input type="radio" name="hybrid-secret-mode" value="recoveryKey" ${secretMode === 'recoveryKey' ? 'checked' : ''} />
            Clé de récupération
          </label>
        </div>
        <div class="field" data-passphrase-field style="${secretMode === 'passphrase' ? '' : 'display:none;'}">
          <label for="hybrid-restore-passphrase">Phrase secrète</label>
          <input id="hybrid-restore-passphrase" type="password" autocomplete="current-password" spellcheck="false" />
        </div>
        <div class="field" data-key-field style="${secretMode === 'recoveryKey' ? '' : 'display:none;'}">
          <label for="hybrid-restore-recovery-key">Clé de récupération</label>
          <input id="hybrid-restore-recovery-key" type="text" autocomplete="off" spellcheck="false" placeholder="Groupes séparés par des espaces ou tirets" />
          <span class="hint">Les espaces et tirets sont acceptés.</span>
        </div>
      </fieldset>
      <p data-hybrid-form-error role="alert" style="display:none;margin:12px 0 0;color:var(--rouge,#b42318);"></p>
      <div style="display:flex;justify-content:flex-end;gap:10px;margin-top:16px;">
        <button type="button" class="btn btn-ghost" data-hybrid-cancel-form>Annuler</button>
        <button type="button" class="btn btn-primary" data-hybrid-confirm-restore>Restaurer</button>
      </div>
    `;
    enhancePasswordFields(formHost);

    const passphraseField = formHost.querySelector<HTMLElement>('[data-passphrase-field]');
    const keyField = formHost.querySelector<HTMLElement>('[data-key-field]');
    const passphraseInput = formHost.querySelector<HTMLInputElement>('#hybrid-restore-passphrase');
    const keyInput = formHost.querySelector<HTMLInputElement>('#hybrid-restore-recovery-key');
    const formError = formHost.querySelector<HTMLElement>('[data-hybrid-form-error]');
    const confirmBtn = formHost.querySelector<HTMLButtonElement>('[data-hybrid-confirm-restore]');

    const syncMode = (mode: SecretMode): void => {
      secretMode = mode;
      if (passphraseField) passphraseField.style.display = mode === 'passphrase' ? '' : 'none';
      if (keyField) keyField.style.display = mode === 'recoveryKey' ? '' : 'none';
      if (mode === 'passphrase') passphraseInput?.focus();
      else keyInput?.focus();
    };

    formHost.querySelectorAll<HTMLInputElement>('input[name="hybrid-secret-mode"]').forEach((radio) => {
      radio.addEventListener('change', () => {
        if (radio.value === 'passphrase' || radio.value === 'recoveryKey') syncMode(radio.value);
      });
    });

    formHost.querySelector('[data-hybrid-cancel-form]')?.addEventListener('click', () => {
      clearSensitiveInput(passphraseInput);
      clearSensitiveInput(keyInput);
      selectedBackup = null;
      formHost.style.display = 'none';
      formHost.innerHTML = '';
    });

    confirmBtn?.addEventListener('click', () => {
      void (async () => {
        if (restoring || !selectedBackup) return;
        if (formError) {
          formError.style.display = 'none';
          formError.textContent = '';
        }

        const tenantId = ctx.tenantContext.tryGetTenant()?.id;
        if (!tenantId) {
          if (formError) {
            formError.textContent = 'Aucun dossier actif : impossible de restaurer.';
            formError.style.display = 'block';
          }
          return;
        }

        let secret: HybridUnlockSecret;
        if (secretMode === 'passphrase') {
          const passphrase = passphraseInput?.value.trim() ?? '';
          if (!passphrase) {
            if (formError) {
              formError.textContent = 'Saisissez votre phrase secrète.';
              formError.style.display = 'block';
            }
            passphraseInput?.focus();
            return;
          }
          secret = { passphrase };
        } else {
          const recoveryKey = keyInput?.value.trim() ?? '';
          if (!recoveryKey) {
            if (formError) {
              formError.textContent = 'Saisissez votre clé de récupération.';
              formError.style.display = 'block';
            }
            keyInput?.focus();
            return;
          }
          secret = { recoveryKey };
        }

        const confirmed = await confirmAction({
          title: 'Confirmer la restauration',
          message:
            'Une sauvegarde de sécurité de vos données actuelles sera créée. Continuer ?',
          confirmLabel: 'Continuer',
          cancelLabel: 'Annuler',
          danger: true,
        });
        if (!confirmed) return;

        restoring = true;
        if (confirmBtn) {
          confirmBtn.disabled = true;
          confirmBtn.textContent = 'Restauration…';
        }
        formHost.querySelectorAll<HTMLInputElement | HTMLButtonElement>('input, button').forEach((el) => {
          el.disabled = true;
        });
        resultHost.style.display = 'none';
        setProgressStarting();

        const backupId = selectedBackup.id;
        const restoreDeps = {
          db: ctx.db,
          tenantId,
          onProgress: (step: HybridRestoreStep) => setProgressFromService(step),
        };

        const emptyFailReport = (message: string): HybridRestoreReport => ({
          ok: false,
          backupId,
          backupCreatedAt: null,
          stepsDone: [],
          expectedCounts: {},
          actualCounts: {},
          restored: {},
          safetyBackup: null,
          tenantRemapped: false,
          files: { restored: 0, failed: 0 },
          databaseUnchanged: true,
          message,
        });

        const unlockForm = (): void => {
          restoring = false;
          if (confirmBtn) {
            confirmBtn.disabled = false;
            confirmBtn.textContent = 'Restaurer';
          }
          formHost
            .querySelectorAll<HTMLInputElement | HTMLButtonElement>('input, button')
            .forEach((el) => {
              el.disabled = false;
            });
        };

        let report: HybridRestoreReport;
        try {
          // Chemin normal : refuse sans allowTenantTransfer (jamais proposé par défaut).
          report = await HybridRestoreService.restore({ backupId, secret }, restoreDeps);
        } catch (err) {
          report = emptyFailReport(describeHybridTransportError(err));
        } finally {
          clearSensitiveInput(passphraseInput);
          clearSensitiveInput(keyInput);
        }

        if (report.ok) {
          setProgressFromService('done');
          showResult(report);
          unlockForm();
          return;
        }

        showResult(report);

        if (report.errorCode === 'BACKUP_TENANT_MISMATCH') {
          const targetTenantName = currentTenantDisplayName(ctx);
          if (!targetTenantName) {
            unlockForm();
            return;
          }
          // Opt-in explicite uniquement après mismatch : secret encore en mémoire locale.
          openTenantTransferConfirmDialog({
            backupTenantName: backupTenantNameFromMessage(report.message),
            targetTenantName,
            onCancel: () => unlockForm(),
            onConfirm: async () => {
              restoring = true;
              if (confirmBtn) {
                confirmBtn.disabled = true;
                confirmBtn.textContent = 'Transfert…';
              }
              setProgressStarting();
              let transferReport: HybridRestoreReport;
              try {
                transferReport = await HybridRestoreService.restore(
                  { backupId, secret, allowTenantTransfer: true },
                  restoreDeps,
                );
              } catch (err) {
                transferReport = emptyFailReport(describeHybridTransportError(err));
              }
              if (transferReport.ok) setProgressFromService('done');
              showResult(transferReport);
              unlockForm();
            },
          });
          return;
        }

        unlockForm();
      })();
    });
  };

  listHost.addEventListener('click', (event) => {
    const target = event.target;
    if (!(target instanceof HTMLElement)) return;
    const btn = target.closest<HTMLButtonElement>('[data-hybrid-restore]');
    if (!btn || restoring) return;
    const id = btn.getAttribute('data-hybrid-restore');
    if (!id) return;
    const meta = listedItems.find((b) => b.id === id);
    if (meta) renderSecretForm(meta);
  });

  void (async () => {
    try {
      const items = await HybridRestoreService.listAvailable();
      listedItems = items;
      if (items.length === 0) {
        listStatus.textContent = 'Aucune sauvegarde Hybride disponible pour cette licence.';
        listHost.innerHTML = '';
        return;
      }
      listStatus.textContent = `${items.length} sauvegarde${items.length > 1 ? 's' : ''} disponible${items.length > 1 ? 's' : ''}.`;
      new DataTable<HybridBackupMeta>(
        [
          {
            key: 'created_at',
            label: 'Date',
            sortable: true,
            format: (v) => esc(formatBackupDate(String(v ?? ''))),
          },
          {
            key: 'size_bytes',
            label: 'Taille',
            align: 'right',
            format: (v) => esc(formatSize(typeof v === 'number' ? v : undefined)),
          },
          {
            key: 'app_version',
            label: 'Version app',
            format: (v) => esc(v ? String(v) : '—'),
          },
          {
            key: 'id',
            label: 'Action',
            format: (_v, row) =>
              `<button type="button" class="btn btn-primary btn-sm" data-hybrid-restore="${esc(row.id)}">Restaurer</button>`,
          },
        ],
        items,
        {
          emptyLabel: 'Aucune sauvegarde Hybride disponible.',
          caption: 'Sauvegardes Hybride',
        },
      ).mount(listHost);
    } catch (err) {
      listStatus.textContent = describeHybridTransportError(err);
      listHost.innerHTML = '';
    }
  })();
}

/** Ouvre la restauration Hybride dans une modale large (Paramètres). */
export function openHybridBackupRestoreDialog(ctx: AppContext): Promise<void> {
  return new Promise((resolve) => {
    const modal = document.createElement('nexxus-modal') as ModalEl;
    modal.setAttribute('title', 'Restaurer une sauvegarde Hybride');
    modal.setAttribute('size', 'lg');

    const host = document.createElement('div');
    modal.appendChild(host);

    const footer = document.createElement('div');
    footer.slot = 'footer';
    footer.style.display = 'flex';
    footer.style.justifyContent = 'flex-end';
    const closeBtn = document.createElement('button');
    closeBtn.type = 'button';
    closeBtn.className = 'btn btn-ghost';
    closeBtn.textContent = 'Fermer';
    footer.appendChild(closeBtn);
    modal.appendChild(footer);

    document.body.appendChild(modal);

    let settled = false;
    const finish = (): void => {
      if (settled) return;
      settled = true;
      modal.close();
      modal.remove();
      resolve();
    };

    closeBtn.addEventListener('click', finish);
    modal.addEventListener('close', finish);
    renderHybridBackupRestorePanel(host, ctx);
    modal.open();
  });
}
